import React, { useContext, useEffect, useRef, useState } from "react";
import { decimalFormatter } from "../Utils/Formatter";
import {
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import DestinationOfTheDay from "../Components/DestinationOfTheDay";
import { FaRoute, FaStopwatch } from "react-icons/fa";
import { MdAirplanemodeActive } from "react-icons/md";
import Card from "../Components/Card";
import { UserGroupIcon, UserIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import EmptyMap from "../Components/Maps/EmptyMap";
import SocketContext from "../Contexts/SocketContext";
import useOsSystemIcons from "../Hooks/useOsSystemIcons";
import UserStat from "./Analytics/UserStat";
import Spinner from "../Components/Loaders/Spinner";
import LoadingBar from "../Components/Loaders/LoadingBar";
import { domainWithHeadroom, toNumericSeries } from "../Utils/chartScale";
const Dashboard = () => {
  const { getOsSystemIcon } = useOsSystemIcons();
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const [data, setData] = useState([]);
  const previousCountRef = useRef(0);
  const socket = useContext(SocketContext);

  // active_users kommt als String aus der API (SUM(...) ist bigint, node-pg
  // liefert das als String). Recharts wuerde die Y-Domain daraus per
  // Stringvergleich bestimmen und die Flaeche oben abschneiden.
  //
  // Hier zusaetzlich eine explizite Domain: Die Achse ist ausgeblendet, und
  // ueber jedem Punkt steht ein Wertlabel - das braucht Platz, den die
  // automatische Skalierung nicht einplant.
  const playerCountData = React.useMemo(
    () => toNumericSeries(data?.player_count, "active_users"),
    [data?.player_count]
  );
  const playerCountDomain = React.useMemo(
    () => domainWithHeadroom(playerCountData, "active_users", 0.2),
    [playerCountData]
  );

  const pieColors = ["#A87BCC", "#d5b6ee"]; // Beispiel: zwei Farben, die sich abwechseln
  const pieData = data?.acft_distribution?.map((item) => {
    const totalFlights = parseInt(item.total_flights, 10);
    const overall = data.acft_distribution.reduce(
      (sum, { total_flights }) => sum + parseInt(total_flights, 10),
      0
    );
    return {
      acft: item.acft,
      count: totalFlights,
      percent: overall ? (totalFlights / overall) * 100 : 0,
    };
  });
  useEffect(() => {
    socket?.on("analysisData", (data) => {
      setData(data);
      setLoading(false);
      previousCountRef.current = data.active_flights;
    });
    socket?.emit("showDashboard");

    return () => {
      socket?.emit("stopShowingDashboard");
    };
  }, [socket]);

  return (
    <>
      <div className="2xl:grid grid-cols-3 gap-4">
        <h1 className="font-bold text-3xl  col-span-3">Dashboard Overview</h1>
        <div className="col-span-2 hidden 2xl:block">
          <h2 className="font-bold self-end ">Weekly Stats</h2>
          <p className=" text-sm text-gray-600">
            Insights over the last 7 days.
          </p>
        </div>
        <div className="col-start-3 pl-4 hidden 2xl:block">
          <h2 className="font-bold self-end ">Live Stats</h2>
          <p className=" text-sm text-gray-600">Insights of current flights </p>
        </div>
      </div>
      <div className="2xl:grid grid-cols-3 mt-4 gap-4 ">
        <div className="col-span-2 lg:grid grid-cols-3 gap-4 content-start">
          <div className="lg:grid grid-cols-2 xl:grid-cols-4 gap-4 col-span-3 content-start space-y-4 lg:space-y-0">
            <UserStat
              loading={loading}
              icon={
                <UserGroupIcon
                  className="h-6 w-6 text-white"
                  aria-hidden="true"
                />
              }
              partial={data?.stats?.number_of_players}
              label="Unique Players"
            />
            <UserStat
              loading={loading}
              icon={
                <MdAirplanemodeActive
                  className="h-6 w-6 text-white rotate-45"
                  aria-hidden="true"
                />
              }
              partial={data?.stats?.number_of_flights}
              label="Flights"
            />
            <UserStat
              loading={loading}
              icon={
                <FaStopwatch
                  className="h-6 w-6 text-white "
                  aria-hidden="true"
                />
              }
              partial={data?.stats?.average_flight_duration_in_minutes}
              label="⌀ Duration"
              partialLabel=" Min"
            />
            <UserStat
              loading={loading}
              icon={
                <FaRoute className="h-6 w-6 text-white" aria-hidden="true" />
              }
              partial={data?.stats?.average_flight_distance_in_km}
              label={"⌀ Distance"}
              partialLabel=" km"
            />
          </div>
          <div className="col-span-3 mt-4 ">
            <Card>
              <h2 className="font-bold mb-2">Player Activity</h2>
              <p className="mb-4 text-sm text-gray-600">
                Trend of daily active players for the last seven days.
              </p>
              {loading ? (
                <div className="flex justify-center items-center w-full h-32 ">
                  <Spinner height="h-8 w-8" />
                </div>
              ) : (
                <ResponsiveContainer height={150}>
                  <AreaChart
                    data={playerCountData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#6b21a8"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#6b21a8"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" hide />
                    <YAxis hide domain={playerCountDomain} allowDataOverflow={false} />
                    <Area
                      type="monotone"
                      dataKey="active_users"
                      stroke="#6b21a8"
                      fill="url(#colorUv)"
                      dot={{ stroke: "#A87BCC", strokeWidth: 2 }}
                      label={{ position: "top", fill: "#A87BCC" }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
          <div className="col-span-3 mt-4 ">
            <Card>
              <h2 className="font-bold mb-2">Aircraft distribution</h2>
              <p className="mb-4 text-sm text-gray-600">
                These are your users 10 most popular aircraft choices of the
                last seven days.
              </p>

              {loading ? (
                <div className="flex justify-center items-center w-full h-32 ">
                  <Spinner height="h-8 w-8" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 ">
                  <div>
                    <ol className="place-self-center w-full">
                      <li
                        key={"header"}
                        className="grid grid-cols-12 text-sm font-semibold"
                      >
                        <div className="col-span-5 py-2">
                          <p className="text-sm  text-gray-600">{"Aircraft"}</p>
                        </div>
                        <div className="col-span-3  py-2 text-right">
                          <p className="">{"# Flights"}</p>
                        </div>
                        <div className="col-span-4 py-2 text-right">
                          <p className="">{"# Flights in %"}</p>
                        </div>
                      </li>
                    </ol>
                    <ol className="place-self-center w-full overflow-y-scroll h-64">
                      {data?.acft_distribution?.map((item, index) => (
                        <li
                          key={item.acft}
                          className="grid grid-cols-12 text-sm"
                        >
                          <div className="col-span-5  border-t border-gray-200  py-2 flex items-center space-x-2">
                            <img
                              src={`/Images/acft_markers/acft_${item.acft}.png`}
                              className="h-6 w-6 rotate-45 flex-shrink-0"
                              alt="acft_of_the_day"
                            />
                            <p className="text-sm  text-gray-600">
                              {item.acft}
                            </p>
                          </div>
                          <div className="col-span-3  text-right border-t border-gray-200  py-2">
                            <p className="">
                              {decimalFormatter.format(item.total_flights)}
                            </p>
                          </div>
                          <div className="col-span-4  text-right border-t border-gray-200  py-2">
                            <p className="">
                              {(
                                (parseInt(item.total_flights) /
                                  data?.acft_distribution?.reduce(
                                    (n, { total_flights }) =>
                                      n + parseInt(total_flights),
                                    0
                                  )) *
                                100
                              ).toFixed(0) + "%"}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <ResponsiveContainer height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="count"
                        nameKey="acft"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ payload }) => payload.acft}
                      >
                        {pieData?.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>
        </div>
        <div className="col-start-3 pl-4  2xl:hidden  mt-4 ">
          <h2 className="font-bold self-end ">Live Stats</h2>
          <p className=" text-sm text-gray-600">Insights of current flights </p>
        </div>
        <div className="2xl:border-l border-gray-300 2xl:pl-4 mt-4 2xl:mt-0">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center space-x-4">
                <img
                  src={`/Images/acft_markers/acft_${
                    loading ? "a320" : data?.top_acft?.acft ?? "a320"
                  }.png`}
                  className="h-12 w-12 rotate-45"
                  alt="acft_of_the_day"
                />
                {loading ? (
                  <div>
                    <LoadingBar height="h-4" width="w-8" />
                    <div className="mt-1">
                      <LoadingBar
                        height="h-4 "
                        width="w-12"
                        color={"bg-gray-300"}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold">
                      {data?.top_acft?.acft ?? "Active flights"}
                    </p>
                    <p className="text-sm text-gray-600 whitespace-nowrap">
                      {decimalFormatter.format(data?.top_acft?.count ?? 0)}{" "}
                      flights
                    </p>
                  </div>
                )}
              </div>
            </Card>
            <Card>
              <div className="flex items-center space-x-4">
                {loading
                  ? getOsSystemIcon("ios", "h-12 w-12")
                  : getOsSystemIcon(
                      data?.top_os_system?.os_system ?? "ios",
                      "h-12 w-12"
                    )}
                <div>
                  {loading ? (
                    <>
                      <LoadingBar height="h-4" width="w-8" />
                      <div className="mt-1">
                        <LoadingBar
                          height="h-4 "
                          width="w-12"
                          color={"bg-gray-300"}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-bold">
                        {data?.top_os_system?.os_system ?? "ios"}
                      </p>
                      <p className="text-sm text-gray-600 whitespace-nowrap">
                        {decimalFormatter.format(
                          data?.top_os_system?.count ?? 0
                        )}{" "}
                        players
                      </p>{" "}
                    </>
                  )}
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center space-x-4">
                <FaRoute className="h-12 w-12" />

                {loading ? (
                  <div>
                    <LoadingBar height="h-4" width="w-8" />
                    <div className="mt-1">
                      <LoadingBar
                        height="h-4 "
                        width="w-12"
                        color={"bg-gray-300"}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold">
                      {decimalFormatter.format(
                        data?.longest_current_route?.length?.toFixed(0) ?? 0
                      ) + " km"}
                    </p>
                    <p className="text-sm text-gray-600 whitespace-nowrap">
                      Longest Route
                    </p>
                  </div>
                )}
              </div>
            </Card>
            <Card>
              <div className="flex items-center space-x-4">
                <FaStopwatch className="h-12 w-12" />

                {loading ? (
                  <div>
                    <LoadingBar height="h-4" width="w-8" />
                    <div className="mt-1">
                      <LoadingBar
                        height="h-4 "
                        width="w-12"
                        color={"bg-gray-300"}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold">
                      {(
                        parseFloat(
                          data?.longest_active_session?.duration ?? 0
                        ) / 60
                      ).toFixed(2) + " hours"}
                    </p>
                    <p className="text-sm text-gray-600 whitespace-nowrap">
                      Longest Session
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
          <div className="h-64">
            <Card>
              <Link className="h-full relative group " to="/map">
                <EmptyMap ref={mapRef} defaultZoom={1} />
                <div className="absolute bg-black bg-opacity-60 h-full w-full inset-0 rounded-lg"></div>
                <div className="absolute inset-0 flex items-center justify-center h-full w-full  text-white rounded-lg">
                  <div className="">
                    <div className="flex items-center space-x-12">
                      <div className="flex items-center space-x-2  border-gray-200 rounded-md p-1 px-4">
                        <div className="bg-red-600 shadow shadow-red-600 animate-pulse rounded-full h-4 w-4"></div>
                        <CountUp
                          start={previousCountRef.current}
                          end={data?.active_flights}
                        />
                        <UserIcon className="h-8 w-8" />{" "}
                      </div>
                    </div>
                    <p className="group-hover:underline text-lg  mt-4">
                      Go to Live Map
                    </p>
                  </div>
                </div>
              </Link>
            </Card>
          </div>
        </div>
      </div>
      {/* <div className="mt-8">
       

        <div className="xl:grid grid-cols-5 gap-8 h-full w-full mt-8">
          <div className="w-full h-full col-span-2">
            
          </div>

          <div className=" ">
            
          </div>
          <div className="w-full"></div>
          
        </div>
      </div> */}
    </>
  );
};

export default Dashboard;

const CustomTooltip = ({ active, payload, label, sum }) => {
  if (active && payload && payload.length) {
    return (
      <Card>
        <p className="text-xs font-bold mb-2">{payload[0]?.name}</p>
        <div className="flex items-center space-x-4">
          <p>{decimalFormatter.format(payload[0].value)}</p>
          <p>{payload[0]?.payload?.percent?.toFixed(0) + "%"}</p>
        </div>
      </Card>
    );
  }
};
